// The app half of the Projects TanStack layer: the chat sidebar's list caches and
// the billing caches a turn moves, bound to the `@heidi/agents/data` hooks. The
// rest of the layer — the queries, the keys, the feedback write — is on the doors.
import type { InfiniteData, QueryClient, UseMutationResult } from '@tanstack/react-query';

import {
  invalidateCreditAllowance,
  invalidateCurrentUserBillingInfo,
  invalidateGamifyTrialExtension,
} from '@heidi/commerce/data';
import type { ChatListResponse } from '@heidi/session/contracts';

import { injected } from '../../data/injected-auth';
import {
  invalidateProjectLists,
  useCreateProject as useCreateProjectCore,
  useDeleteProject as useDeleteProjectCore,
  useSubmitProjectTurn as useSubmitProjectTurnCore,
  useToggleSavedProject as useToggleSavedProjectCore,
  useUpdateProject as useUpdateProjectCore,
  type ProjectCacheReactions,
} from '../../data/projects/hooks';

const chatListKeys = () => injected('chatListQueryKeys');

const invalidateChatSidebarLists = (qc: QueryClient) => {
  const { chats, savedChats } = chatListKeys();
  // Agent chats are persisted in the ``chat`` collection (same row
  // as ``ChatDO`` with ``agent_config_id`` set) and surface in the
  // sidebar's chat list via ``/v2/chat/chats``. Invalidate that
  // query too so create / update / delete on an agent immediately
  // refreshes the sidebar; without this the sidebar stays stale
  // until something else (e.g. focus) refetches.
  void qc.invalidateQueries({ queryKey: [chats] });
  // Saved chats are served by a separate endpoint/query family. V2
  // project chats can be saved, so project create/update/delete/save
  // changes must also refresh the saved section of the chat sidebar.
  void qc.invalidateQueries({ queryKey: [savedChats] });
};

const isFlatChatsQuery = (queryKey: readonly unknown[]): boolean =>
  queryKey[0] === chatListKeys().chats && queryKey[1] !== 'infinite';

const removeChatFromPage = (
  page: ChatListResponse | undefined,
  chatId: string
): ChatListResponse | undefined => {
  if (!page) return page;
  const remaining = page.chats.filter((c) => c.id !== chatId);
  return {
    ...page,
    chats: remaining,
    total: page.total - (page.chats.length - remaining.length),
  };
};

/**
 * Removes a deleted project from both shapes of the chats list (flat and
 * infinite) and from saved chats, and returns the rollback that restores the
 * three snapshots in the same order.
 */
const removeProjectFromChatSidebar = (qc: QueryClient, projectId: string) => {
  const { chats, savedChats } = chatListKeys();
  const previousInfiniteChats = qc.getQueriesData<InfiniteData<ChatListResponse, number>>(
    {
      queryKey: [chats, 'infinite'],
    }
  );
  qc.setQueriesData<InfiniteData<ChatListResponse, number>>(
    { queryKey: [chats, 'infinite'] },
    (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page) => {
          const remaining = page.chats.filter((c) => c.id !== projectId);
          return {
            ...page,
            chats: remaining,
            total: page.total - (page.chats.length - remaining.length),
          };
        }),
      };
    }
  );

  const previousFlatChats = qc.getQueriesData<ChatListResponse>({
    predicate: (query) => isFlatChatsQuery(query.queryKey),
  });
  qc.setQueriesData<ChatListResponse>(
    { predicate: (query) => isFlatChatsQuery(query.queryKey) },
    (old) => removeChatFromPage(old, projectId)
  );

  const previousSavedChats = qc.getQueriesData<ChatListResponse>({
    queryKey: [savedChats],
  });
  qc.setQueriesData<ChatListResponse>({ queryKey: [savedChats] }, (old) =>
    removeChatFromPage(old, projectId)
  );

  return () => {
    for (const [key, data] of previousInfiniteChats) {
      qc.setQueryData(key, data);
    }
    for (const [key, data] of previousFlatChats) {
      qc.setQueryData(key, data);
    }
    for (const [key, data] of previousSavedChats) {
      qc.setQueryData(key, data);
    }
  };
};

/** One module-level object, so the hooks below hand the cores a stable value. */
const projectCacheReactions: ProjectCacheReactions = {
  invalidateOtherLists: invalidateChatSidebarLists,
  removeFromOtherLists: removeProjectFromChatSidebar,
  onCreditAllowanceExhausted: () => {
    void invalidateCreditAllowance();
  },
  onTurnSubmitted: () => {
    // Each turn counts toward the gamify "Ask Heidi" task, and the backend
    // may apply the trial extension inside this request, which moves the
    // trial end date that billing info carries.
    void Promise.all([
      invalidateGamifyTrialExtension(),
      invalidateCurrentUserBillingInfo(),
    ]);
  },
};

export const invalidateLists = (qc: QueryClient) => {
  invalidateProjectLists(qc);
  invalidateChatSidebarLists(qc);
};

export const useCreateProject = () => useCreateProjectCore(projectCacheReactions);

export const useUpdateProject = (projectId: string) =>
  useUpdateProjectCore(projectId, projectCacheReactions);

// The core's mutation context is a private interface. Naming the public return
// keeps declaration emit from reaching back for that name.
export const useDeleteProject = (): UseMutationResult<void, Error, string, unknown> =>
  useDeleteProjectCore(projectCacheReactions);

export const useToggleSavedProject = (projectId: string) =>
  useToggleSavedProjectCore(projectId, projectCacheReactions);

export const useSubmitProjectTurn = (projectId: string) =>
  useSubmitProjectTurnCore(projectId, projectCacheReactions);
